/**
 * Brand-specific configuration for ad serving
 * Contains GAM slot paths, bidder parameters, and language settings for each brand
 */

// Shared bidder configurations that are identical across brands
const SHARED_BIDDERS = {
  rubicon: {
    accountId: 19800
  },
  pubmatic: {
    publisherId: '160629'
  },
  ttd: {
    supplySourceId: 'narcity',
    publisherId: '1'
  },
  ix: {
    desktop: {
      topAd: '301232',
      content: '301230',
      sidebar: '301231'
    },
    mobile: {
      leaderboard: '641191',
      content: '641192'
    }
  }
};

export const NARCITY_CONFIG = {
  brand: 'narcity',
  gamNetwork: '/1020360',
  language: {
    supportsFrench: true
  },
  // RebelMouse "Static pages" section (contact, masthead, policies). Pages in
  // it get no comments, Insights embeds or CDM unit (see isStaticPage).
  staticPagesSectionId: 550003979,
  // RebelMouse USA edition section: the page contract (lib/page/millaPage.js)
  // files its posts under narcity-us.
  usaSectionId: 550004012,
  // Dynamic login wall (plans/dynamic-login-wall.md): sections never walled,
  // besides static pages.
  loginWall: {
    excludedSectionIds: []
  },
  // Native app listings for the mobile-web install sheet; the icon is served
  // from the auth host (plans/publisher-web-app-install-prompt.md).
  appInstall: {
    name: 'Narcity',
    iosAppId: '1291431733',
    androidPackage: 'com.narcitymedia.narcity',
    icon: '/brand/narcity-app-icon.png'
  },
  // Privacy policy pages by reader language. Narcity has no French policy
  // page yet, so French readers get the English one (getPrivacyPolicyUrl).
  privacyPolicy: {
    en: 'https://www.narcity.com/privacy-policy'
  },
  homepageCsa: {
    container: 'cdm-container',
    pubId: 'partner-pub-7289986524496939',
    pageType: 'home',
    resultsPageBaseUrl: 'http://www.narcity.com/search/',
    resultsPageQueryParam: 'q'
  },
  articleCsa: {
    container: 'cdm-container-article',
    pubId: 'partner-pub-7289986524496939',
    pageType: 'article',
    resultsPageBaseUrl: 'http://www.narcity.com/search/',
    resultsPageQueryParam: 'q'
  },
  articlePlacements: {
    mobileListicle: {
      enabled: true,
      minItems: 8,
      firstAfterItem: 2,
      itemInterval: 3,
      maxCount: 6
    },
    desktopAnchor: {
      enabled: true,
      minViewportWidth: 1280,
      slotId: 'desktop-anchor-narcity',
      sizes: [[728, 90]]
    },
    desktopSidebar: {
      slotCount: 2,
      stickySlotIndex: 2,
      stickyTopPx: 90
    }
  },
  // The GAM "Narcity Skin" template (12061831) draws nothing below 1100px, so
  // narrower viewports do not request the skin.
  desktopSkin: {
    minViewportWidth: 1100
  },
  slots: {
    desktop: {
      topAd: 'custom-header-narcity',
      content: 'dt-content-narcity',
      sidebar: 'custom-side-narcity',
      skin: 'custom-skin-narcity',
      brandSnippet: 'custom-brand-snippet-narcity',
      featuredPartner: 'custom-featured-partner-narcity'
    },
    mobile: {
      leaderboard: 'mobile-lb-narcity',
      content: 'bb-content-narcity'
    }
  },
  bidders: {
    appnexus: {
      desktop: {
        topAd: 24115449,
        content: 24115453,
        sidebar: 24115454
      },
      mobile: {
        leaderboard: 24115450,
        content: 24115453
      },
      video: {
        preroll: 24115453,
        interstitial: 24115453
      }
    },
    rubicon: {
      accountId: SHARED_BIDDERS.rubicon.accountId,
      siteId: 368952,
      zones: {
        desktop: {
          topAd: 2010006,
          content: 2010008,
          sidebar: 2010008
        },
        mobile: {
          leaderboard: 2010010,
          content: 2010012
        },
        video: {
          preroll: 2010008,
          interstitial: 2010008
        }
      }
    },
    yahooSsp: {
      dcn: '8a9694c501787845c67d4c0b3c830210',
      pos: '8a9694c501787845c67d4c0d49500212',
      video: {
        preroll: '8a9694c501787845c67d4c0d49500212',
        interstitial: '8a9694c501787845c67d4c0d49500212'
      }
    },
    teads: {
      pageId: 128610,
      placementId: 141215,
      video: {
        preroll: 141215,
        interstitial: 141215
      }
    },
    ix: {
      desktop: {
        topAd: SHARED_BIDDERS.ix.desktop.topAd,
        content: SHARED_BIDDERS.ix.desktop.content,
        sidebar: SHARED_BIDDERS.ix.desktop.sidebar
      },
      mobile: {
        leaderboard: SHARED_BIDDERS.ix.mobile.leaderboard,
        content: SHARED_BIDDERS.ix.mobile.content
      },
      video: {
        preroll: SHARED_BIDDERS.ix.desktop.content,
        interstitial: SHARED_BIDDERS.ix.desktop.content
      }
    },
    pubmatic: {
      publisherId: SHARED_BIDDERS.pubmatic.publisherId,
      adSlots: {
        desktop: {
          topAd: '4099098',
          content: '4099100',
          sidebar: '4099101'
        },
        mobile: {
          leaderboard: '4099099',
          content: '4099100'
        },
        video: {
          preroll: '4099100',
          interstitial: '4099100'
        }
      }
    },
    ttd: {
      supplySourceId: SHARED_BIDDERS.ttd.supplySourceId,
      publisherId: SHARED_BIDDERS.ttd.publisherId,
      video: {
        preroll: 'narcity-stories-preroll',
        interstitial: 'narcity-stories-interstitial'
      }
    }
  },
  proLinkSource: {
    desktop: 'narcity_desktop_ad',
    mobile: 'narcity_mobile_ad'
  }
};

export const MTLBLOG_CONFIG = {
  brand: 'mtlblog',
  gamNetwork: '/1020360',
  language: {
    supportsFrench: false
  },
  // RebelMouse "Static pages" section; see NARCITY_CONFIG.staticPagesSectionId.
  staticPagesSectionId: 551223143,
  // See NARCITY_CONFIG.loginWall: Notices (Montréal and site-wide) and
  // AI-assisted posts from the notice automation (canadian-press-rm
  // src/notices/config.js).
  loginWall: {
    excludedSectionIds: [2263801096, 2263801093, 2271064221]
  },
  // See NARCITY_CONFIG.appInstall.
  appInstall: {
    name: 'MTL Blog',
    iosAppId: '1576382814',
    androidPackage: 'com.narcitymedia.mtlblog',
    icon: '/brand/mtlblog-app-icon.png'
  },
  // Privacy policy pages by reader language (French page live 2026-09-28).
  privacyPolicy: {
    en: 'https://www.mtlblog.com/privacy-policy',
    fr: 'https://www.mtlblog.com/politique-de-confidentialite'
  },
  homepageCsa: {
    container: 'cdm-container',
    pubId: 'partner-pub-7289986524496939',
    pageType: 'home',
    resultsPageBaseUrl: 'http://www.mtlblog.com/search/',
    resultsPageQueryParam: 'q'
  },
  articleCsa: {
    container: 'cdm-container-article',
    pubId: 'partner-pub-7289986524496939',
    pageType: 'article',
    resultsPageBaseUrl: 'http://www.mtlblog.com/search/',
    resultsPageQueryParam: 'q'
  },
  articlePlacements: {
    mobileListicle: {
      enabled: true,
      minItems: 8,
      firstAfterItem: 2,
      itemInterval: 3,
      maxCount: 6
    },
    desktopAnchor: {
      enabled: true,
      minViewportWidth: 1280,
      slotId: 'desktop-anchor-mtlblog',
      sizes: [[728, 90]]
    },
    desktopSidebar: {
      slotCount: 2,
      stickySlotIndex: 2,
      stickyTopPx: 90
    }
  },
  // The GAM "Narcity Skin" template (12061831) draws nothing below 1100px, so
  // narrower viewports do not request the skin.
  desktopSkin: {
    minViewportWidth: 1100
  },
  slots: {
    desktop: {
      topAd: 'Leaderboard_Master',
      content: 'dt-content-mtlblog',
      sidebar: 'custom-side-mtlblog',
      skin: 'Skin',
      brandSnippet: 'custom-brand-snippet-mtlblog',
      featuredPartner: 'custom-featured-partner-mtlblog'
    },
    mobile: {
      leaderboard: 'mobile-lb-mtlblog',
      content: 'bb-content-mtlblog'
    }
  },
  bidders: {
    appnexus: {
      desktop: {
        topAd: 24115473,
        content: 24115488,
        sidebar: 24115489
      },
      mobile: {
        leaderboard: 24115476,
        content: 24115478
      },
      video: {
        preroll: 24115488,
        interstitial: 24115488
      }
    },
    rubicon: {
      accountId: SHARED_BIDDERS.rubicon.accountId,
      siteId: 368954,
      zones: {
        desktop: {
          topAd: 2010014,
          content: 2010016,
          sidebar: 2010016
        },
        mobile: {
          leaderboard: 2010018,
          content: 2010020
        },
        video: {
          preroll: 2010016,
          interstitial: 2010016
        }
      }
    },
    yahooSsp: {
      dcn: '8a9690d901787845c2834c5ddb99022e',
      pos: '8a969d1e01787845bdc44c5e764d0203',
      video: {
        preroll: '8a969d1e01787845bdc44c5e764d0203',
        interstitial: '8a969d1e01787845bdc44c5e764d0203'
      }
    },
    teads: {
      pageId: 128654,
      placementId: 141266,
      video: {
        preroll: 141266,
        interstitial: 141266
      }
    },
    ix: {
      desktop: {
        topAd: SHARED_BIDDERS.ix.desktop.topAd,
        content: SHARED_BIDDERS.ix.desktop.content,
        sidebar: SHARED_BIDDERS.ix.desktop.sidebar
      },
      mobile: {
        leaderboard: SHARED_BIDDERS.ix.mobile.leaderboard,
        content: SHARED_BIDDERS.ix.mobile.content
      },
      video: {
        preroll: SHARED_BIDDERS.ix.desktop.content,
        interstitial: SHARED_BIDDERS.ix.desktop.content
      }
    },
    pubmatic: {
      publisherId: SHARED_BIDDERS.pubmatic.publisherId,
      adSlots: {
        desktop: {
          topAd: '4099098',
          content: '4099100',
          sidebar: '4099101'
        },
        mobile: {
          leaderboard: '4099099',
          content: '4099100'
        },
        video: {
          preroll: '4099100',
          interstitial: '4099100'
        }
      }
    },
    ttd: {
      supplySourceId: SHARED_BIDDERS.ttd.supplySourceId,
      publisherId: SHARED_BIDDERS.ttd.publisherId,
      video: {
        preroll: 'mtlblog-stories-preroll',
        interstitial: 'mtlblog-stories-interstitial'
      }
    }
  },
  proLinkSource: {
    desktop: 'mtlblog_desktop_ad',
    mobile: 'mtlblog_mobile_ad'
  }
};

// Helper to get config by brand name
export function getBrandConfig(brand) {
  if (brand === 'narcity') return NARCITY_CONFIG;
  if (brand === 'mtlblog') return MTLBLOG_CONFIG;
  throw new Error(`Unknown brand: ${brand}`);
}

// The privacy policy page for the brand whose auth host issued this page's
// identity, in the reader's language ('en' | 'fr'). Unknown hosts get Narcity's.
export function getPrivacyPolicyUrl(issuer, language) {
  const urls = getBrandConfig(/mtlblog/i.test(String(issuer || '')) ? 'mtlblog' : 'narcity').privacyPolicy;
  return urls[language] || urls.en;
}

// Export shared bidders for reference
export { SHARED_BIDDERS };
